import {Injectable, OnDestroy} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {LoanApplication} from '../../@models/loan-application.model';
import {Contact} from '../../@models/contact.model';
import {ContactDBService} from '../../@db/contact-db.service';
import {BehaviorSubject, Observable, ReplaySubject, Subject} from 'rxjs';
import {EmploymentStatus} from '../../@models/employment-status.model';
import {Lead} from '../../@models/lead.model';
import {HttpErrorResponse} from '@angular/common/http';
import {LoanApplicationDBService} from '../../@db/loan-application-db.service';
import {Router} from '@angular/router';
import {BranchDBService} from '../../@db/branch-db.service';
import {Branch} from '../../@models/branch.model';
import {AnalyticsService} from '../../analytics.service';
import {PersistenceService} from 'angular-persistence';
import * as Raven from 'raven-js';


@Injectable()
export class LoanApplicationService implements OnDestroy {
  submitted = new Subject<LoanApplication.Model>();

  form = this.fb.group({
    branch: null,
    application_source: null,
    has_comaker: null,
    comaker_relation: null,
    comaker_name: null,
    comaker_mobile_number: this.fb.control(null, Validators.required, Contact.Validation.forMobile(this.contact)),
    target_loan_amount: null,
    target_loan_duration: null,
    estimated_monthly_payment: null,
    loan_purpose: null,
    has_accepted: null,

    slider_config: this.fb.group({
      interest_rate: null,
      loan_fee_rate: null,
      grace_period: null,
      check_fee: null,
      target_loan_amount: this.fb.group({
        min: null,
        max: null,
        step: null,
      }),
      target_loan_duration: this.fb.group({
        min: null,
        max: null,
        step: null,
      }),
    }),

    employment_info: this.fb.group({
      job_title: null,
      job_title_link: null,
      job_category: null,
      employment_type: null,
      employer_name: null,
      manning_agency: null,
      agency: null,
      seaman_principal: null,
      seaman_ship: null,
      destined_country: null,
      employment_channel: null,
      recruitment_agency: null,
      monthly_salary: this.fb.control(null, [Validators.min(20000)]),
    }),

    current_status: this.fb.group({
      employment_status: null,
      average_monthly_remittance: this.fb.control(null, [Validators.min(1)]),
      datetime_to_philippines: '',
      datetime_to_work: '',
      datetime_contract_end: this.fb.control({ value: '', disabled: false }),
      started_ofw: [''],
      unlimited_contract: false,
      tentative_departure_date: '',
    }),

    personal_info: this.fb.group({
      first_name: ['', Validators.required],
      middle_name: '',
      last_name: ['', Validators.required],
      suffix: '',
      birthday: ['', Validators.required],
      mobile_numbers: this.fb.array([
        this.fb.control(null, Validators.required, Contact.Validation.forMobile(this.contact)),
        this.fb.control(null, null, Contact.Validation.forMobile(this.contact)),
      ]),
      landline_numbers: this.fb.array([
        this.fb.control(null, null, Contact.Validation.forLandline(this.contact)),
      ]),
      email_addresses: this.fb.array([
        this.fb.control(null, Validators.required, Contact.Validation.forEmail(this.contact)),
      ]),
      city: ['', Validators.required],
      province: ['', Validators.required],
    }),
  });

  baseValues = {
    record_source: Lead.Source.leadForm,
    origin_user_agent: navigator.userAgent,
    origin_url: location.href,
    origin_viewport_width: window.innerWidth,
    origin_viewport_height: window.innerHeight,
  };

  // Posting States
  _retryIn5seconds = 10;
  retryCountDown = new BehaviorSubject<number>(0);
  retryingPosting = this.retryCountDown.map(c => c > 0);
  retryNow = new Subject<void>();

  // Pagination
  pageIndex = new BehaviorSubject<number>(0);
  routePrefix = new ReplaySubject<string>(1);

  // Branch
  defaultBranch: Observable<Branch.Model> = this.routePrefix
    .map(p => this.persistence.get('branch') || p === 'cebu' && 'cebu' || null)
    .switchMap(alias => alias && this.branchDb.getFromAlias(alias) || Observable.of(null));

  constructor(private fb: FormBuilder,
              private contact: ContactDBService,
              private loanDB: LoanApplicationDBService,
              private route: Router,
              private branchDb: BranchDBService,
              private analytics: AnalyticsService,
              private persistence: PersistenceService) {

    // Set the Slider variables
    this.form.get('employment_info.employment_type')
      .valueChanges
      .startWith(LoanApplication.EmploymentType.land)
      .filter(t => !!t)
      .subscribe((employmentType: LoanApplication.EmploymentType) => {
        this.form.patchValue({
          target_loan_amount: LoanApplication.LoanAmountBounds[employmentType].default,
          target_loan_duration: LoanApplication.LoanDurationBounds[employmentType].default,
          slider_config: {
            interest_rate: LoanApplication.Interest[employmentType],
            loan_fee_rate: LoanApplication.LoanFee[employmentType],
            grace_period: LoanApplication.GracePeriod[employmentType],
            check_fee: LoanApplication.CheckFee,
            target_loan_amount: {
              min: LoanApplication.LoanAmountBounds[employmentType].min,
              max: LoanApplication.LoanAmountBounds[employmentType].max,
              step: LoanApplication.LoanAmountBounds[employmentType].step,
            },
            target_loan_duration: {
              min: LoanApplication.LoanDurationBounds[employmentType].min,
              max: LoanApplication.LoanDurationBounds[employmentType].max,
              step: LoanApplication.LoanDurationBounds[employmentType].step,
            },
          },
        });
      });

    this.form.valueChanges.startWith(this.form.value)
      .subscribe(value => Raven.setExtraContext({loanForm: {...value, ...this.baseValues}}));

    // Calculate month Amortization
    Observable.merge(
      this.form.get('target_loan_amount').valueChanges,
      this.form.get('target_loan_duration').valueChanges,
      this.form.get('slider_config').valueChanges,
    ).debounceTime(5).subscribe(() => {
      const config = <LoanApplication.SliderConfig>this.form.get('slider_config').value;
      this.form.get('estimated_monthly_payment').setValue(
        LoanApplication.getMonthlyAmortization(
          this.form.get('target_loan_amount').value,
          this.form.get('target_loan_duration').value,
          config.interest_rate,
          config.loan_fee_rate,
          config.grace_period,
          config.check_fee));
    });

    // Set to Page 1 if /apply-now
    this.routePrefix.filter(p => p === 'apply-now')
      .subscribe(() => this.pageIndex.next(1));

    // If /cebu, assign to localStorage
    this.routePrefix.filter(p => p === 'cebu')
      .subscribe(() => this.persistence.set('branch', 'cebu'));

    this.resetForm();
  }

  ngOnDestroy() {
    Raven.setExtraContext({loanForm: null});
  }

  resetForm() {
    Observable
      .combineLatest(this.defaultBranch, this.routePrefix)
      .first()
      .subscribe(([defaultBranch, routePrefix]: [Branch.Model, string]) => {
        this.form.reset();

        // Branch
        if (defaultBranch) {
          this.form.get('branch').setValue(defaultBranch.pk);
        }

        // Employment Info
        const employmentInfo = <LoanApplication.CreateVars>{
          employment_type: LoanApplication.EmploymentType.land,
        };
        if (routePrefix === 'seaman' || routePrefix === 'seaman-loan') {
          employmentInfo.employment_type = LoanApplication.EmploymentType.sea;
        }
        this.form.get('employment_info').patchValue(employmentInfo);

        this.form.markAsPristine();
      });
  }

  get isBalikMangagawaOrAbroad(): boolean {
    return [
      EmploymentStatus.KnownStatuses.balik_manggagawa,
      EmploymentStatus.KnownStatuses.working_flyback,
      EmploymentStatus.KnownStatuses.working,
    ].includes(this.form.get('current_status.employment_status').value);
  }

  get value(): LoanApplication.CreateVars {
    const vars = this.form.value;
    const current_status = vars.current_status;
    const employment_info = vars.employment_info;
    const personal_info = vars.personal_info;

    const final = <LoanApplication.CreateVars>{
      branch: vars.branch,
      employment_type: vars,
      target_loan_amount: vars.target_loan_amount,
      target_loan_duration: vars.target_loan_duration,
      estimated_monthly_payment: vars.estimated_monthly_payment,
      slider_config: vars.slider_config,
      loan_purpose: vars.loan_purpose,
    };
    const employment_type = <LoanApplication.EmploymentType>employment_info.employment_type;
    const employment_channel = <LoanApplication.EmploymentChannel>employment_info.employment_channel;
    const employment_status = <EmploymentStatus.KnownStatuses>current_status.employment_status;

    final.job_title = employment_info.job_title;
    final.monthly_salary = employment_info.monthly_salary;
    if (vars.job_title_link) {
      final.job_title_link = employment_info.job_title_link;
    }

    final.job_category = employment_info.job_category;
    final.employment_type = employment_type;
    if (employment_type === LoanApplication.EmploymentType.land) {
      final.employment_channel = employment_channel;
      final.destined_country = employment_info.destined_country;
      final.employer_name = employment_info.employer_name;
      if (employment_channel === LoanApplication.EmploymentChannel.agency) {
        final.recruitment_agency = employment_info.recruitment_agency;
      }
    }
    if (employment_type === LoanApplication.EmploymentType.sea) {
      final.manning_agency = employment_info.manning_agency;
      final.seaman_principal = employment_info.seaman_principal;
      final.seaman_ship = employment_info.seaman_ship;
    }

    if (employment_info.agency) {
      final.agency = employment_info.agency;
    }

    // Money Figures
    final.employment_status = employment_status;
    if (employment_status !== EmploymentStatus.KnownStatuses.looking) {
      if (this.isBalikMangagawaOrAbroad) {
        final.average_monthly_remittance = current_status.average_monthly_remittance;
      }
    }

    // Career Dates
    if ([EmploymentStatus.KnownStatuses.working_flyback,
      EmploymentStatus.KnownStatuses.balik_manggagawa].includes(employment_status)) {
      final.datetime_to_work = current_status.datetime_to_work;
    }
    if (EmploymentStatus.KnownStatuses.working_flyback === employment_status) {
      final.datetime_to_philippines = current_status.datetime_to_philippines;
    }
    if ([EmploymentStatus.KnownStatuses.working,
      EmploymentStatus.KnownStatuses.working_flyback,
      EmploymentStatus.KnownStatuses.balik_manggagawa,
      EmploymentStatus.KnownStatuses.sea_onboard].includes(employment_status)) {
      final.datetime_contract_end = current_status.datetime_contract_end;
      final.unlimited_contract = current_status.unlimited_contract || false;
    }
    if ([EmploymentStatus.KnownStatuses.balik_manggagawa,
      EmploymentStatus.KnownStatuses.working_flyback,
      EmploymentStatus.KnownStatuses.working].includes(employment_status)) {
      final.started_ofw = current_status.started_ofw;
    }
    if ([EmploymentStatus.KnownStatuses.done_requirements,
      EmploymentStatus.KnownStatuses.sea_signed].includes(employment_status)) {
      final.tentative_departure_date = current_status.tentative_departure_date
    }

    final.first_name = personal_info.first_name;
    if (personal_info.middle_name) {
      final.middle_name = personal_info.middle_name;
    }
    final.last_name = personal_info.last_name;
    final.suffix = personal_info.suffix;
    final.birthday = personal_info.birthday;
    final.mobile_numbers = personal_info.mobile_numbers;
    final.landline_numbers = personal_info.landline_numbers;
    final.email_addresses = personal_info.email_addresses;
    final.city = personal_info.city;
    final.province = personal_info.province;

    if (vars.has_comaker) {
      final.has_comaker = vars.has_comaker;
      final.comaker_relation = vars.comaker_relation;
      final.comaker_name = vars.comaker_name;
      final.comaker_mobile_number = vars.comaker_mobile_number;
    }

    final.application_source = vars.application_source;
    final.has_accepted = vars.has_accepted;

    return <LoanApplication.CreateVars>{...final, ...this.baseValues};
  }

  postLoanApplication(): Observable<LoanApplication.Model> {
    return this.loanDB.applyForLoan(this.value)
      .retryWhen((errorSubject: Subject<HttpErrorResponse>) =>
        errorSubject
          .flatMap((error: HttpErrorResponse) => {
            // If Server times out, re retry every 5 seconds
            if (error.status === 0 || error.status === 504) {
              return Observable.merge(
                Observable.interval(1000)
                  .map(n => this._retryIn5seconds - n)
                  .do(n => this.retryCountDown.next(n))
                  .take(this._retryIn5seconds).last(),
                this.retryNow.first(),
              ).first();
            } else {
              // Otherwise let it burn
              return Observable.throwError(error);
            }
          }))
      .finally(() => this.retryCountDown.next(0))
      .do(loan => this.submitted.next(loan));
  }

  forward(): void {
    this.pageIndex.first().subscribe(currentPage => {
      const newIndex = currentPage + 1;
      this.pageIndex.next(newIndex);
      this.analytics.dataLayer({
        virtualPageURL: `/index/${newIndex}`,
        virtualPageTitle: `Loan Application – Step ${newIndex}`,
        virtualtrigger: 'loan_app_next_page_ok',
      });
    });
  }

  backward(): void {
    this.pageIndex.first().subscribe(currentPage => this.pageIndex.next(currentPage - 1));
  }
}
